import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';

interface ApiService {
  get: (sectorId: string) => Promise<{ data: any }>;
  update?: (sectorId: string, data: any) => Promise<{ data: any }>;
  post?: (sectorId: string, data: any) => Promise<{ data: any }>;
}

interface ValidationErrors {
  [key: string]: string;
}

interface UseChartConfigOptions<TConfig> {
  sectorId: string;
  apiService: ApiService;
  defaultConfig: TConfig;
  configKey: string;
  validateConfig?: (config: TConfig) => ValidationErrors;
  transformBackendData?: (backendData: any, defaultConfig: TConfig) => TConfig;
  transformPayload?: (config: TConfig) => any;
  messages?: {
    loadError?: string;
    saveSuccess?: string;
    saveError?: string;
    validationError?: string;
  };
}

interface UseChartConfigReturn<TConfig> {
  formConfig: TConfig;
  appliedConfig: TConfig;
  loading: boolean;
  errors: ValidationErrors;
  handleConfigChange: (field: keyof TConfig, value: string | number) => void;
  handleSave: () => Promise<boolean>;
}

const useChartConfig = <TConfig extends Record<string, any>>({
  sectorId,
  apiService,
  defaultConfig,
  configKey,
  validateConfig,
  transformBackendData,
  transformPayload,
  messages = {},
}: UseChartConfigOptions<TConfig>): UseChartConfigReturn<TConfig> => {
  const [formConfig, setFormConfig] = useState<TConfig>({ ...defaultConfig });
  const [appliedConfig, setAppliedConfig] = useState<TConfig>({ ...defaultConfig });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { enqueueSnackbar } = useSnackbar();

  const defaultMessages = {
    loadError: messages.loadError || 'No se pudo cargar la configuración',
    saveSuccess: messages.saveSuccess || 'Configuración guardada correctamente',
    saveError: messages.saveError || 'Error al guardar la configuración',
    validationError: messages.validationError || 'Por favor, resolver errores en el formulario',
  };

  const fetchConfig = useCallback(async () => {
    if (!sectorId) return;

    setLoading(true);
    try {
      const { data } = await apiService.get(sectorId);
      if (data?.[configKey]) {
        const backendConfig = data[configKey];
        const newConfig = transformBackendData
          ? transformBackendData(backendConfig, defaultConfig)
          : { ...defaultConfig, ...backendConfig };

        setFormConfig(newConfig);
        setAppliedConfig(newConfig);

        if (validateConfig) {
          setErrors(validateConfig(newConfig));
        }
      }
    } catch (err) {
      enqueueSnackbar(defaultMessages.loadError, {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [sectorId, enqueueSnackbar, configKey, defaultMessages.loadError]);

  const handleConfigChange = useCallback((field: keyof TConfig, value: string | number) => {
    setFormConfig((prev) => {
      const newConfig = {
        ...prev,
        [field]: value,
      };

      if (validateConfig) {
        setErrors(validateConfig(newConfig));
      }

      return newConfig;
    });
  }, [validateConfig]);

  const handleSave = useCallback(async () => {
    if (validateConfig) {
      const validationErrors = validateConfig(formConfig);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        enqueueSnackbar(defaultMessages.validationError, {
          variant: 'error',
        });
        return false;
      }
    }

    try {
      setLoading(true);
      const payload = transformPayload ? transformPayload(formConfig) : formConfig;

      if (apiService.update) {
        await apiService.update(sectorId, payload);
      } else if (apiService.post) {
        await apiService.post(sectorId, payload);
      }

      enqueueSnackbar(defaultMessages.saveSuccess, {
        variant: 'success',
      });
      setAppliedConfig(formConfig);
      return true;
    } catch (err) {
      enqueueSnackbar(defaultMessages.saveError, {
        variant: 'error',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    formConfig,
    sectorId,
    enqueueSnackbar,
    validateConfig,
    defaultMessages.saveSuccess,
    defaultMessages.saveError,
    defaultMessages.validationError,
  ]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    formConfig,
    appliedConfig,
    loading,
    errors,
    handleConfigChange,
    handleSave,
  };
};

export default useChartConfig;
